'use client';
import { useCallback, useMemo, useRef, type ReactNode } from 'react';
import { Clock3, Cpu, Folder, Gauge, GitBranch, MessageSquarePlus, PanelRightClose, PanelRightOpen, PenLine, Shrink, Target, X, type LucideIcon } from 'lucide-react';
import { useTranslation } from '../../lib/i18n';
import { plural } from '../../lib/i18n/plural';
import { useBrainRateLimitsAll } from '../../lib/queries';
import { formatTokens, formatDuration } from '../../lib/format';
import { drawnUsageWindows, usageProgressClass, usageMeterValue, usageWindowMeter } from '../../lib/usageMeter';
import { formatCost, contextMeter, providerUsageAfterRead, railProcessVisible } from '../../lib/displayFormat';
import { OAuthUsageRail } from '../settings/OAuthUsageRail';
import { MascotGlyph } from '../../components/ui/SpatialMascot';
import { Dialog, DialogContent } from '../../components/ui/shadcn/dialog';
import { RailDot, RailList, RailMeter, RailNote, RailRow, RailSection } from '../../components/ui/RailPrimitives';
import { Button } from '../../components/ui/Button';
import { useToast } from '../../components/ui/Toast';
import { orderAgents } from './agentPresentation';
import { uncollectedChildResult } from '../../lib/railSelection';
import { Progress } from '../../components/ui/shadcn/progress';
import { ScrollArea } from '../../components/ui/shadcn/scroll-area';
import { Separator } from '../../components/ui/shadcn/separator';
import { focusOverlaySurface, useReturnFocus } from '../../components/ui/overlayStack';
import { cardTasks, cardItemsAddressable } from '../../lib/railTasks';
import { useBrainChat, useBrainChatStatus } from './BrainChatProvider';
import { PluginChatRailSectionHost, useRailTakeovers } from '../../lib/pluginChatUi';
import { useTelemetryRail } from './telemetryRailState';
import { WorkModeMenuGroup } from './ComposerActions';
import { EmptyState } from '../../components/ui/states';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../../components/ui/shadcn/dropdown-menu';
import { goalStatusSnapshot, useGoalElapsed } from './GoalStatus';
import type { BrainGoal } from '../../lib/types';
import type { CardTask } from 'elowen-plugin-ui-kit';

/** The owl presides over the rail the way it tops the CLI panel — and it is not decoration: it mirrors
 *  the agent, breathing while a turn runs and settling when it does not, so the rail reads as inhabited
 *  rather than a dashboard.
 *
 *  It is also the door to the command field: clicking it opens a dropdown of the slash commands worth
 *  reaching for mid-conversation — the work modes as a radio group, the one-off actions beneath a
 *  separator. The rows come from the daemon's catalog (`GET /brain/commands`, already surface-filtered)
 *  and execute through the controller's own `runSlash` — the menu is a second door onto the composer's
 *  slash menu, never a second implementation of it. A curated command that this account's surface does
 *  not expose simply has no row.
 *
 *  The flat glyph rather than the full WebGL mascot: that scene frames itself at a fixed pixel size, so a
 *  rail this narrow would crop it down to a pair of eyes.
 *
 *  `size` is a plain square in pixels rather than a share of the rail. In the redesigned head the mascot
 *  sits BESIDE the status text instead of above it, so its box is what the header row is built around; a
 *  percentage-sized owl would re-flow that row on every drag. */
const MASCOT_COMMANDS: readonly { name: string; icon: LucideIcon }[] = [
  { name: 'compact', icon: Shrink },
  { name: 'rename', icon: PenLine },
  { name: 'new', icon: MessageSquarePlus },
  { name: 'model', icon: Cpu },
];

/** The BUTTON around the drawing takes the shared default icon step (36px, 44px on a coarse pointer), the
 *  step every neighbouring control in the rail uses, and `size` only sizes the drawing inside it. It used
 *  to state a square of its own in inline pixels, which is why the head measured 44px against a 32px
 *  collapse control beside it. */
function TelemetryMascot({ busy, size }: { busy: boolean; size: number }) {
  const { t } = useTranslation();
  const { commands, runSlash } = useBrainChat();
  const field = t.brainChat.commandField;
  const labels: Record<string, string> = {
    compact: field.compact,
    rename: field.rename,
    new: field.newChat,
    model: field.model,
  };
  // The catalog is surface-filtered, so a curated command the daemon withheld gets no row at all.
  const cataloged = MASCOT_COMMANDS.flatMap(({ name, icon }) => {
    const command = commands.find((c) => c.name === name);
    return command ? [{ command, icon }] : [];
  });
  const modeCommands = commands.filter(command => command.kind === 'mode');
  const actionCommands = cataloged.filter(({ command }) => command.kind !== 'mode');
  const run = (name: string): void => {
    const command = cataloged.find((c) => c.command.name === name)?.command;
    if (command) runSlash(command);
  };
  // The reader's-language hint where the dictionary has one; the catalog's English line otherwise.
  const hints: Record<string, string | undefined> = t.brainChat.commandHints;
  const hintFor = (name: string): string | undefined => hints[name] ?? cataloged.find((c) => c.command.name === name)?.command.description;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          data-testid="telemetry-mascot"
          aria-label={field.open}
          title={field.open}
          className="shrink-0 rounded-full hover:scale-105"
        >
          <span className="flex items-center justify-center" style={{ width: size, height: size }}>
            <MascotGlyph state={busy ? 'saving' : 'idle'} />
          </span>
        </Button>
      </DropdownMenuTrigger>
      {/* `align="start"` keeps the panel anchored to the owl's left edge at either size the trigger
          renders at; Radix's collision handling is what stops it running off the viewport at the rail's
          edge. Click/keyboard only — a hover-open menu has no place above a live conversation. */}
      <DropdownMenuContent align="start" sideOffset={6} className="min-w-52">
        <DropdownMenuLabel>{field.title}</DropdownMenuLabel>
        {modeCommands.length > 0 ? <WorkModeMenuGroup testIdPrefix="telemetry-menu" /> : null}
        {modeCommands.length > 0 && actionCommands.length > 0 ? <DropdownMenuSeparator /> : null}
        {actionCommands.map(({ command, icon: Icon }) => (
          <DropdownMenuItem key={command.name} data-testid={`telemetry-menu-${command.name}`} onSelect={() => run(command.name)}>
            <Icon size={14} aria-hidden className="mt-0.5 shrink-0" />
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="truncate">{labels[command.name] ?? command.name}</span>
              {hintFor(command.name) ? <span className="truncate text-meta text-muted-foreground">{hintFor(command.name)}</span> : null}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Shared live readings for the expanded rail and compact strip.
 * Provider limits use the focused snapshot's pi provider id against the owner-wide map, including
 * read-only children. Both densities share explicit card claims and the same actionable row groups. */
function useTelemetryReadings() {
  const { pluginSessionId, usageProvider } = useBrainChat();
  const { usage, telemetry, goal, subagents, workflows, cards, processes: sessionProcesses } = useBrainChatStatus();
  const { data: limitsByProvider = {}, error: limitsError } = useBrainRateLimitsAll();
  const limits = providerUsageAfterRead(usageProvider ? limitsByProvider[usageProvider] : null, !!limitsError, Date.now());
  const activeGoal: BrainGoal | null = goal?.status === 'active' ? goal : null;
  // Only children that run or await collection are listed; the rail never offers the rest.
  const liveAgents = useMemo(() => orderAgents(subagents).filter(uncollectedChildResult), [subagents]);
  const runningWorkflows = workflows.filter((workflow) => workflow.status === 'running');
  const processes = sessionProcesses.filter(railProcessVisible);
  const takeovers = useRailTakeovers();
  const cardSections = useMemo(() => {
    const groups = new Map<string, { plugin: string; section: string; data: { tasks: CardTask[] } }>();
    if (!takeovers.ready) return [];
    // Walk stream card order, not declaration order; completed rows still belong in a live group's count.
    for (const card of cards) {
      const owners = takeovers.byCard.get(card.id);
      if (!owners || !cardItemsAddressable(card)) continue;
      const tasks = cardTasks(cards, card.id);
      for (const owner of owners) {
        const key = JSON.stringify([owner.plugin, owner.section]);
        const group = groups.get(key);
        if (group) group.data.tasks.push(...tasks);
        else groups.set(key, { ...owner, data: { tasks: [...tasks] } });
      }
    }
    return [...groups.values()].filter(group => group.data.tasks.some(task => task.status !== 'completed'));
  }, [cards, takeovers]);
  const context = contextMeter(usage?.percent);
  return { pluginSessionId, usage, telemetry, limits, activeGoal, liveAgents, runningWorkflows, processes, cardSections, context };
}

/** Both rail densities open an individual child's transcript, tasks or a workflow's DAG. */
function useRailOpen(onOpenWorkflow?: (id: string) => void): (target: string) => void {
  const { commands, runSlash, focusSubagentSession } = useBrainChat();
  const { subagents } = useBrainChatStatus();
  const { t } = useTranslation();
  const { toast } = useToast();
  const rail = useTelemetryRail();
  return useCallback((target: string): void => {
    rail?.setMobileOpen(false);
    if (target.startsWith('agent:')) {
      const sessionId = target.slice('agent:'.length);
      const agent = subagents.find(candidate => candidate.sessionId === sessionId);
      void focusSubagentSession(sessionId, agent?.name || agent?.task)
        .catch(() => toast(t.brainChat.searchOpenError, 'error'));
    }
    if (target === 'tasks') {
      const command = commands.find((candidate) => candidate.name === 'tasks');
      if (command) void runSlash(command);
    }
    if (target.startsWith('workflow:')) onOpenWorkflow?.(target.slice('workflow:'.length));
  }, [commands, focusSubagentSession, onOpenWorkflow, rail, runSlash, subagents, t.brainChat.searchOpenError, toast]);
}

function TelemetryBody({ onOpenWorkflow }: { onOpenWorkflow?: (id: string) => void }) {
  const { t } = useTranslation();
  const { pluginSessionId, usage, telemetry, limits, activeGoal, liveAgents, runningWorkflows, processes, cardSections, context } = useTelemetryReadings();
  const openPlugin = useRailOpen(onOpenWorkflow);
  const mcp = telemetry.mcp;
  const sections = [
    usage !== null,
    activeGoal !== null,
    !!limits?.windows.length,
    cardSections.length > 0,
    runningWorkflows.length > 0,
    liveAgents.length > 0,
    processes.length > 0,
    (mcp?.length ?? 0) > 0,
    telemetry.chatRailSections.length > 0,
  ];
  const goalTurns = activeGoal && activeGoal.turn_budget > 0
    ? `${activeGoal.turns_used}/${activeGoal.turn_budget} ${plural(t.telemetry.goalTurns, activeGoal.turn_budget)}`
    : activeGoal ? `${activeGoal.turns_used} ${plural(t.telemetry.goalTurns, activeGoal.turns_used)}` : undefined;
  const goalElapsed = useGoalElapsed(activeGoal);
  const goalMeta = goalTurns ? `${goalTurns} · ${formatDuration(goalElapsed)}` : undefined;
  const goalFacts = goalStatusSnapshot(activeGoal);
  const subgoals = goalFacts?.subgoals;

  if (!sections.some(Boolean)) {
    return <EmptyState title={t.telemetry.empty} />;
  }

  return (
    <div className="flex w-full min-w-0 flex-col gap-4 px-4 py-3">
      {usage ? (
        <RailSection testId="telemetry-context" label={t.brainChat.context} icon={<Gauge aria-hidden />}>
          <RailList>
            {context.percent == null
              ? <RailRow label={t.telemetry.contextUsed} value={context.label} />
              : <RailMeter label={t.telemetry.contextUsed} percent={context.percent} value={context.label} tone="pressure" />}
            <RailRow label={t.telemetry.contextTokens} value={`${usage.tokens == null ? '—' : formatTokens(usage.tokens)} / ${formatTokens(usage.contextWindow)}`} />
            <RailRow label={t.telemetry.cost} value={formatCost(usage.cost, 2)} />
          </RailList>
        </RailSection>
      ) : null}

      {activeGoal ? (
        <RailSection testId="telemetry-goal" label={t.telemetry.goal} icon={<Target aria-hidden />}>
          <RailList>
            <RailRow lead={<Target size={14} className="text-primary" aria-hidden />} label={activeGoal.goal} title={activeGoal.goal} value={goalMeta} />
            {subgoals ? <RailRow label={t.telemetry.goalSubgoals} value={`${subgoals.done}/${subgoals.total}`} /> : null}
          </RailList>
          {goalFacts?.evidence ? (
            <RailNote title={goalFacts.evidence}>{t.telemetry.goalProgress.replace('{text}', goalFacts.evidence)}</RailNote>
          ) : null}
        </RailSection>
      ) : null}

      {limits?.windows.length ? (
        <RailSection testId="telemetry-limits" label={t.telemetry.limits} icon={<Clock3 aria-hidden />} count={limits.planType || undefined}>
          <OAuthUsageRail usage={limits} />
        </RailSection>
      ) : null}

      {cardSections.map(({ plugin, section, data }) => (
        <PluginChatRailSectionHost key={JSON.stringify([plugin, section])} plugin={plugin} section={section} variant="expanded" sessionId={pluginSessionId} data={data} open={openPlugin} />
      ))}

      {runningWorkflows.length > 0 || liveAgents.length > 0 ? (
        <PluginChatRailSectionHost plugin="subagent" section="subagent" variant="expanded" sessionId={pluginSessionId} data={{ agents: liveAgents, workflows: runningWorkflows }} open={openPlugin} />
      ) : null}

      {processes.length > 0 ? (
        <PluginChatRailSectionHost
          plugin="terminal"
          section="terminal"
          variant="expanded"
          sessionId={pluginSessionId}
          data={{ processes }}
          open={openPlugin}
        />
      ) : null}

      <PluginChatRailSectionHost plugin="mcp" section="mcp" variant="expanded" sessionId={pluginSessionId} data={{ servers: mcp ?? [] }} open={openPlugin} />

      {telemetry.chatRailSections.map(({ plugin, section, data }) => (
        <PluginChatRailSectionHost key={JSON.stringify([plugin, section])} plugin={plugin} section={section} variant="expanded" sessionId={pluginSessionId} data={data} open={openPlugin} />
      ))}
    </div>
  );
}

/** The pinned head: who is here and what they are doing, sitting on the viewport's top edge.
 *
 *  The mascot moved from a ~230px decorative block to a 56px glyph with the status BESIDE it rather than
 *  under it, which is what bought the scrolling band its screen. */
function TelemetryHead({ busy, collapsible, onToggle, onClose }: {
  busy: boolean;
  collapsible: boolean;
  onToggle?: () => void;
  /** The phone sheet's close control; the sheet has no title bar of its own. */
  onClose?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div data-testid="telemetry-head" className="flex items-center gap-3 px-4 py-3">
      <TelemetryMascot busy={busy} size={36} />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-card-title text-foreground">{t.telemetry.title}</span>
        <span className="flex min-w-0 items-center gap-1.5 text-meta text-muted-foreground">
          <RailDot tone={busy ? 'primary' : 'muted'} live={busy} />
          <span className="truncate">{busy ? t.telemetry.statusRunning : t.telemetry.statusIdle}</span>
        </span>
      </div>
      {collapsible && onToggle ? (
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
          aria-label={t.telemetry.collapse}
          title={t.telemetry.collapse}
          aria-expanded
          className="shrink-0 rounded-md"
          data-testid="telemetry-collapse"
        >
          <PanelRightClose size={16} aria-hidden />
        </Button>
      ) : null}
      {onClose ? (
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          aria-label={t.telemetry.close}
          title={t.telemetry.close}
          className="shrink-0 rounded-md"
        >
          <X size={18} aria-hidden />
        </Button>
      ) : null}
    </div>
  );
}

/** The pinned foot: the directory and branch this conversation works in, on the viewport's bottom edge.
 *  Where it works is ambient information — it belongs on a status bar, not in the middle of the metrics
 *  it never changes with. */
function TelemetryFoot() {
  const { t } = useTranslation();
  const { telemetry } = useBrainChatStatus();
  const project = telemetry.project;
  if (!project?.cwd && !project?.branch) return null;
  return (
    <div className="px-4 py-2">
      <section data-testid="telemetry-project">
        <RailList>
          {project?.cwd ? (
            <RailRow lead={<Folder size={14} className="text-subtle-foreground" aria-hidden />} label={project.cwd} title={project.cwd} mono />
          ) : null}
          {project?.branch ? (
            // A branch name is one unbreakable token (`agent/chat-rail-no-scroll-20260818`); the row
            // truncates it like the path above, with the full name on the title.
            <RailRow
              lead={<GitBranch size={14} className="text-subtle-foreground" aria-hidden />}
              label={<span className="text-primary">{project.branch}</span>}
              title={`${t.telemetry.branch}: ${project.branch}`}
              mono
            />
          ) : null}
        </RailList>
      </section>
    </div>
  );
}

/** One instrument in the compact rail. It keeps the 52px strip readable without inventing a second
 *  dashboard: icon = section identity, optional micro-meter = pressure, mono value = the one number worth
 *  seeing without expanding. Every instrument opens the full rail for detail; native title carries the
 *  complete label for mouse users while `aria-label` names the same action to assistive tech. */
function CompactTelemetryItem({ id, icon: Icon, label, value, hint, progress, tone = 'muted', onOpen }: {
  id: string;
  icon: LucideIcon;
  label: string;
  value?: string;
  /** The full reading for the native title and the accessible name, when the strip's own `value` is a
   *  shortened form of it (a percentage standing in for an exact amount). */
  hint?: string;
  progress?: number;
  tone?: 'muted' | 'primary';
  onOpen?: () => void;
}) {
  const title = hint ?? (value ? `${label}: ${value}` : label);
  const content = (
    <>
      <Icon
        size={18}
        className={tone === 'primary' ? 'text-primary' : 'text-subtle-foreground'}
        aria-hidden
      />
      {progress != null ? (
        <Progress
          className="h-1 w-9"
          value={Math.max(0, Math.min(100, progress))}
          indicatorValue={usageMeterValue(progress)}
          indicatorClassName={usageProgressClass(progress)}
          aria-hidden
        />
      ) : null}
      {value ? <span className="max-w-full truncate text-meta tabular-nums text-muted-foreground">{value}</span> : null}
    </>
  );
  const className = "flex h-auto w-11 flex-col items-center gap-1.5 rounded-md px-0.5 py-2";
  if (!onOpen) {
    return <div data-testid={`telemetry-compact-${id}`} className={className} title={title}>{content}</div>;
  }
  return (
    <Button
      variant="ghost"
      size="sm"
      data-testid={`telemetry-compact-${id}`}
      onClick={onOpen}
      aria-label={title}
      title={title}
      className={className}
    >
      {content}
    </Button>
  );
}

/** The collapsed 52px instrument strip. It mirrors every section that can appear in the expanded body:
 *  context, subscription windows, active goal/work, processes, MCP, LSP and project identity. Sections
 *  without data stay absent exactly as they do in the full rail. The middle is independently scrollable,
 *  so even a short desktop can reach every instrument without widening the conversation gutter. */
function TelemetryStub({ busy, onToggle, onOpenWorkflow }: { busy: boolean; onToggle?: () => void; onOpenWorkflow?: (id: string) => void }) {
  const { t, locale } = useTranslation();
  const { pluginSessionId, usage, telemetry, limits, activeGoal, liveAgents, runningWorkflows, processes: ownProcesses, cardSections, context } = useTelemetryReadings();
  const openPlugin = useRailOpen(onOpenWorkflow);
  const projectTitle = [telemetry.project?.cwd, telemetry.project?.branch].filter(Boolean).join(' · ');

  return (
    <div data-testid="telemetry-stub" className="flex h-full min-h-0 flex-col items-center bg-background">
      {/* The head is exactly as tall as the top bar, so its rule continues the bar's rule across the rail. */}
      <div className="flex h-[calc(var(--topbar-height)-1px)] w-full shrink-0 items-center justify-center">
        <TelemetryMascot busy={busy} size={32} />
      </div>
      <Separator className="w-full shrink-0 bg-sidebar-border" />
      {onToggle ? (
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
          aria-label={t.telemetry.expand}
          title={t.telemetry.expand}
          aria-expanded={false}
          className="my-2 shrink-0 rounded"
          data-testid="telemetry-collapse"
        >
          <PanelRightOpen size={18} aria-hidden />
        </Button>
      ) : null}
      <ScrollArea
        data-testid="telemetry-compact-scroll"
        className="min-h-0 w-full flex-1 [&_[data-slot=scroll-area-scrollbar]]:hidden"
      >
        <div className="flex w-full flex-col items-center gap-1 pb-2">
          {usage ? (
            <CompactTelemetryItem
              id="context"
              icon={Gauge}
              label={t.brainChat.context}
              value={context.label}
              progress={context.percent ?? undefined}
              onOpen={onToggle}
            />
          ) : null}
          {limits ? drawnUsageWindows(limits.windows).map((window, index) => {
            const pct = Math.max(0, Math.min(100, window.usedPercent));
            const meter = usageWindowMeter(window, t, locale);
            return (
              <CompactTelemetryItem
                key={`${window.kind ?? 'time'}-${window.windowMinutes ?? 'window'}-${index}`}
                id={`limit-${index}`}
                icon={Clock3}
                label={`${t.telemetry.limits} · ${meter.label}`}
                value={`${Math.round(pct)}%`}
                hint={`${t.telemetry.limits} · ${meter.label}: ${meter.value ?? `${Math.round(pct)}%`}`}
                tone="muted"
                progress={pct}
                onOpen={onToggle}
              />
            );
          }) : null}
          {activeGoal ? (
            <CompactTelemetryItem
              id="goal"
              icon={Target}
              label={activeGoal.goal}
              value={activeGoal.turn_budget > 0 ? `${activeGoal.turns_used}/${activeGoal.turn_budget}` : String(activeGoal.turns_used)}
              tone="primary"
              onOpen={onToggle}
            />
          ) : null}
          {cardSections.map(({ plugin, section, data }) => (
            <PluginChatRailSectionHost key={JSON.stringify([plugin, section])} plugin={plugin} section={section} variant="compact" sessionId={pluginSessionId} data={data} open={openPlugin} />
          ))}
          {runningWorkflows.length > 0 || liveAgents.length > 0 ? (
            <PluginChatRailSectionHost plugin="subagent" section="subagent" variant="compact" sessionId={pluginSessionId} data={{ agents: liveAgents, workflows: runningWorkflows }} open={openPlugin} />
          ) : null}
          {ownProcesses.length > 0 ? (
            <PluginChatRailSectionHost
              plugin="terminal"
              section="terminal"
              variant="compact"
              sessionId={pluginSessionId}
              data={{ processes: ownProcesses }}
              open={() => onToggle?.()}
            />
          ) : null}
          <PluginChatRailSectionHost plugin="mcp" section="mcp" variant="compact" sessionId={pluginSessionId} data={{ servers: telemetry.mcp ?? [] }} open={() => onToggle?.()} />
          {telemetry.chatRailSections.map(({ plugin, section, data }) => (
            <PluginChatRailSectionHost key={JSON.stringify([plugin, section])} plugin={plugin} section={section} variant="compact" sessionId={pluginSessionId} data={data} open={() => onToggle?.()} />
          ))}
        </div>
      </ScrollArea>
      {projectTitle ? (
        <>
          <Separator className="my-2 w-7 shrink-0" />
          <CompactTelemetryItem id="project" icon={GitBranch} label={projectTitle} tone="primary" onOpen={onToggle} />
        </>
      ) : null}
    </div>
  );
}

/** Head, body and foot as one continuous band, separated by hairlines rather than by card edges.
 *
 *  The head and the foot are pinned and the middle is the only thing that scrolls, which is what lets the
 *  rail sit flush on all three viewport edges: there is no outer scroller to clip it and no frame inset
 *  to keep it off the glass. */
function TelemetryRailContent({ busy, collapsible, onToggle, onClose, onOpenWorkflow }: {
  busy: boolean;
  collapsible: boolean;
  onToggle?: () => void;
  onClose?: () => void;
  onOpenWorkflow?: (id: string) => void;
}) {
  return (
    <div className="flex h-full min-h-0 flex-col">
      <TelemetryHead busy={busy} collapsible={collapsible} onToggle={onToggle} onClose={onClose} />
      <Separator />
      <ScrollArea data-testid="telemetry-scroll" className="min-h-0 flex-1">
        <TelemetryBody onOpenWorkflow={onOpenWorkflow} />
      </ScrollArea>
      <Separator />
      <TelemetryFoot />
    </div>
  );
}

/** The phone slide-over, on the shadcn `Dialog` (Radix): the dialog role, the focus trap, Escape and the
 *  layer order among several open overlays are Radix's, so this file no longer writes any of them.
 *
 *  This dialog renders in place inside the chat shell. Radix's aria-hidden sweep walks the ancestor
 *  chain, as with the current ConversationSwitcherModal. useOverlayIsolation marks body siblings inert
 *  and would also disable this dialog's shell ancestor. useReturnFocus restores the opener because
 *  there is no Dialog.Trigger. */
function TelemetryDrawer({ label, onClose, children }: { label: string; onClose?: () => void; children: ReactNode }) {
  const surfaceRef = useRef<HTMLDivElement>(null);
  const { restoreFocus } = useReturnFocus();
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose?.(); }}>
      <div
        className="overlay-layer-drawer fixed inset-0"
        // Radix's modal content sets `pointer-events: none` on <body> and re-enables them on itself;
        // this layer would inherit the block and the backdrop below would stop answering the click that
        // dismisses the drawer. Opting back in is what `DialogOverlay` does for the same reason.
        style={{ pointerEvents: 'auto' }}
      >
        <div className="absolute inset-0 bg-scrim" onClick={onClose} aria-hidden />
        <DialogContent
          ref={surfaceRef}
          // A right rail that owns its own internal scrolling, which none of the primitive's presentations
          // describes; the geometry stays here. Only the geometry: `presentation={null}` drops the shape
          // classes, but `.overlay-surface` is in the variant BASE and still paints the ground, the
          // border colour and the raised shadow.
          presentation={null}
          aria-label={label}
          aria-describedby={undefined}
          data-testid="telemetry-drawer"
          // Full-bleed on a phone rather than a 18rem sliver: the same three-zone rail, given the whole
          // screen instead of being squeezed into the margin of a conversation it is covering anyway.
          className="animate-drawer-in absolute inset-y-0 right-0 flex w-full flex-col overflow-hidden border-l border-border sm:max-w-sm"
          // The backdrop above already owns dismissal, and it is the only owner that knows a nested
          // overlay's backdrop must not close its parent — the rail raises both a process modal and the
          // command field from inside itself.
          onInteractOutside={(event) => event.preventDefault()}
          // The panel is something to read, so focus anchors on the surface rather than on the close
          // button Radix would pick; the opener gets it back on the way out.
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            if (surfaceRef.current) focusOverlaySurface(surfaceRef.current);
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            restoreFocus();
          }}
        >
          {children}
        </DialogContent>
      </div>
    </Dialog>
  );
}

/** The chat telemetry rail — the web counterpart of the CLI's right-hand panel. It holds information
 *  rather than asking for attention: quiet labels, one meter vocabulary, no colour unless a number is
 *  under pressure.
 *
 *  `column` is the desktop dock and `drawer` is the phone overlay. NEITHER owns its width any more: the
 *  dock is a `ResizablePanel` in the shell (see components/shell/Shell.tsx), which is what lets it sit
 *  flush against the top, right and bottom edges of the viewport instead of starting below the chat
 *  header. This component fills whatever box its host gives it, so the same content works in a 52px stub,
 *  a 340px dock and a full-screen sheet. */
export function TelemetryPanel({ variant, open = false, collapsed = false, onClose, onToggleCollapsed, onOpenWorkflow }: {
  variant: 'column' | 'drawer';
  open?: boolean;
  /** Desktop only: render the 52px stub instead of the full rail. */
  collapsed?: boolean;
  onClose?: () => void;
  onToggleCollapsed?: () => void;
  /** Open the navigable DAG view for a workflow row. Absent → the rows are inert (they still report the
   *  running DAGs), so the rail never depends on a host that has no such view to show. */
  onOpenWorkflow?: (id: string) => void;
}) {
  const { t } = useTranslation();
  const { busy } = useBrainChatStatus();

  if (variant === 'drawer') {
    // Mounted only while open, like the history drawer: a closed drawer leaves nothing focusable behind.
    if (!open) return null;
    return (
      <TelemetryDrawer label={t.telemetry.title} onClose={onClose}>
        {/* No title bar of its own: the rail's head already names the panel and carries the close
            control, so the sheet does not say "Telemetry" twice. */}
        <div className="min-h-0 flex-1">
          <TelemetryRailContent busy={busy} collapsible={false} onClose={onClose} onOpenWorkflow={onOpenWorkflow} />
        </div>
      </TelemetryDrawer>
    );
  }

  return (
    <aside
      aria-label={t.telemetry.title}
      data-testid="telemetry-column"
      data-collapsed={collapsed || undefined}
      // The panel that hosts this owns the width; `w-full` makes the rail consume it instead of shrink-
      // wrapping its contents and leaving an empty strip at the viewport edge. The only painted edge is the
      // resize handle on the left.
      className="flex h-full w-full min-h-0 flex-col overflow-hidden bg-background"
    >
      {collapsed
        ? <TelemetryStub busy={busy} onToggle={onToggleCollapsed} onOpenWorkflow={onOpenWorkflow} />
        : <TelemetryRailContent busy={busy} collapsible onToggle={onToggleCollapsed} onOpenWorkflow={onOpenWorkflow} />}
    </aside>
  );
}
